import { configureStore } from '@reduxjs/toolkit';
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';
import authSlice from '../features/auth/authSlice';
import uploadSlice from '../features/upload/uploadSlice';
import dashboardSlice from '../features/dashboard/dashboardSlice';
import browseSlice from '../features/browse/browseSlice';
import sharingSlice from '../features/sharing/sharingSlice';
import borrowRequestsSlice from '../features/borrowRequests/borrowRequestsSlice';
import groupsSlice from '../features/sharing/groupsSlice';
import itemExtrasSlice from '../features/inventory/itemExtrasSlice';
import custodySlice from '../features/inventory/custodySlice';
import segmentSlice from '../features/segment/segmentSlice';
import notificationsSlice from '../features/notifications/notificationsSlice';
// FE #124 — Scan History feature
import scansSlice from '../features/scans/scansSlice';
// FE #259 — Founsi AI chat tab
import founsiSlice from '../features/founsi/founsiSlice';
// FE #261 PR1 — thread history drawer (list + resume)
import founsiHistorySlice from '../features/founsi/founsiHistorySlice';
// FE-255 PR6 — refresh stale slice caches when new notifications arrive
import { notificationInvalidationMiddleware } from './notificationInvalidationMiddleware';
// Search-UX §4.4 — persisted recent-searches list
import recentSearchesSlice, {
  hydrateRecents,
  readPersistedRecents,
} from '../features/browse/recentSearchesSlice';

export const store = configureStore({
  reducer: {
    auth: authSlice,
    upload: uploadSlice,
    dashboard: dashboardSlice,
    browse: browseSlice,
    sharing: sharingSlice,
    borrowRequests: borrowRequestsSlice,
    groups: groupsSlice,
    itemExtras: itemExtrasSlice,
    custody: custodySlice,
    segment: segmentSlice,
    notifications: notificationsSlice,
    scans: scansSlice,
    recentSearches: recentSearchesSlice,
    founsi: founsiSlice,
    founsiHistory: founsiHistorySlice,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST'],
      },
    }).concat(notificationInvalidationMiddleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Dev-only: expose the store on window for QA / debugging. Stripped by
// minifier in production via __DEV__ check (standard RN/Expo global).
if (typeof window !== 'undefined' && (global as any).__DEV__ !== false) {
  (window as any).__store__ = store;
}

// Search-UX §4.4 — hydrate the persisted recent-searches list once
// at app boot.  Localstorage is synchronous so this is a single
// dispatch with no race; on native we get an empty list (no-op).
store.dispatch(hydrateRecents(readPersistedRecents()));
